import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { X, Check } from 'lucide-react';
import { Input } from './ui/Input';
import { useTagsApi } from '../apis/tags-apis';
import { Tag } from '../types';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  readOnly?: boolean;
  className?: string;
}

interface TagSelectorRef {
  getPendingTags: () => string[];
  processPendingTags: () => Promise<void>;
}

// Custom hook for tag filtering and search
const useTagFiltering = (allTags: Tag[], inputValue: string) => {
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);

  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = allTags.filter(tag => 
        tag.tagName.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredTags(filtered);
    } else {
      setFilteredTags(allTags);
    }
  }, [inputValue, allTags]);

  return filteredTags;
};

// Custom hook for pending tag management
const usePendingTags = (selectedTags: string[], onChange: (tags: string[]) => void, tagsApi: any) => {
  const [pendingTags, setPendingTags] = useState<string[]>([]);

  const addPendingTag = (tagName: string) => {
    if (!selectedTags.includes(tagName) && !pendingTags.includes(tagName)) {
      setPendingTags(prev => [...prev, tagName]);
      onChange([...selectedTags, tagName]);
    }
  };

  const removePendingTag = (tagName: string) => {
    setPendingTags(prev => prev.filter(tag => tag !== tagName));
  };

  const processPendingTags = async () => {
    try {
      if (pendingTags.length === 0) return;
      
      for (const tagName of pendingTags) {
        try {
          await tagsApi.createTag(tagName);
        } catch (error) {
          console.error(`Error creating tag "${tagName}":`, error);
        }
      }
      
      setPendingTags([]);
    } catch (error) {
      console.error('Error processing pending tags:', error);
    }
  };

  return {
    pendingTags,
    addPendingTag,
    removePendingTag,
    processPendingTags,
    setPendingTags
  };
};

// Custom hook for tag data management
const useTagData = (tagsApi: any) => {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setIsLoading(true);
        const tags = await tagsApi.getTags();
        setAllTags(tags);
      } catch (error) {
        console.error('Error fetching tags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTags();
  }, []);

  return { allTags, setAllTags, isLoading };
};

// Utility functions
const normalizeTagName = (input: string): string => {
  return input.trim().replace(/\s+/g, '');
};

const getTagClassName = (tag: string, pendingTags: string[]): string => {
  const isPending = pendingTags.includes(tag);
  return `flex items-center gap-1 ${
    isPending 
      ? 'bg-blue-50 text-blue-800 border border-dashed border-blue-300' 
      : 'bg-blue-100 text-blue-800'
  } px-2 py-1 rounded-md text-sm`;
};

// Dropdown content component
const TagDropdown: React.FC<{
  isOpen: boolean;
  readOnly: boolean;
  isLoading: boolean;
  filteredTags: Tag[];
  selectedTags: string[];
  inputValue: string;
  onTagSelect: (tagName: string) => void;
  onAddPendingTag: () => void;
}> = ({ isOpen, readOnly, isLoading, filteredTags, selectedTags, inputValue, onTagSelect, onAddPendingTag }) => {
  if (!isOpen || readOnly) return null;

  return (
    <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
      {isLoading ? (
        <div className="p-2 text-gray-500 text-center">Loading tags...</div>
      ) : (
        <>
          {filteredTags.length > 0 ? (
            filteredTags.map(tag => (
              <div
                key={tag.tagId}
                className={`p-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                  selectedTags.includes(tag.tagName) ? 'bg-blue-50' : ''
                }`}
                onClick={() => onTagSelect(tag.tagName)}
              >
                <span>{tag.tagName}</span>
                {selectedTags.includes(tag.tagName) && <Check size={16} className="text-blue-500" />}
              </div>
            ))
          ) : inputValue.trim() ? (
            <div
              className="p-2 hover:bg-gray-100 cursor-pointer flex items-center text-blue-600"
              onClick={onAddPendingTag}
            >
              <span>Add tag: "{inputValue}"</span>
            </div>
          ) : (
            <div className="p-2 text-gray-500 text-center">No tags found</div>
          )}
        </>
      )}
    </div>
  );
};

// Selected tags display component
const SelectedTagsDisplay: React.FC<{
  selectedTags: string[];
  pendingTags: string[];
  readOnly: boolean;
  onRemoveTag: (tag: string) => void;
}> = ({ selectedTags, pendingTags, readOnly, onRemoveTag }) => (
  <>
    {selectedTags.map(tag => (
      <div key={tag} className={getTagClassName(tag, pendingTags)}>
        <span>{tag}</span>
        {!readOnly && (
          <button
            type="button"
            onClick={() => onRemoveTag(tag)}
            className="text-blue-800 hover:text-blue-600"
          >
            <X size={14} />
          </button>
        )}
      </div>
    ))}
  </>
);

// Helper functions to extract complex conditions
const canSelectTag = (readOnly: boolean, selectedTags: string[], tagName: string): boolean => {
  return !readOnly && !selectedTags.includes(tagName);
};

const canAddPendingTag = (inputValue: string, readOnly: boolean): boolean => {
  return inputValue.trim().length > 0 && !readOnly;
};

const canRemoveTag = (readOnly: boolean): boolean => {
  return !readOnly;
};

const canHandleKeyDown = (readOnly: boolean): boolean => {
  return !readOnly;
};

const shouldHandleEnterKey = (key: string, inputValue: string): boolean => {
  return key === 'Enter' && inputValue.trim().length > 0;
};

const shouldHandleEscapeKey = (key: string): boolean => {
  return key === 'Escape';
};

const findExistingTag = (allTags: Tag[], normalizedTagName: string): Tag | undefined => {
  return allTags.find(
    tag => tag.tagName.toLowerCase() === normalizedTagName.toLowerCase()
  );
};

// Helper functions to extract complex logic
const addTagToSelection = (
  tagName: string,
  selectedTags: string[],
  pendingTags: string[],
  onChange: (tags: string[]) => void,
  removePendingTag: (tagName: string) => void,
  setInputValue: (value: string) => void,
  setIsOpen: (open: boolean) => void
) => {
  onChange([...selectedTags, tagName]);
  
  if (pendingTags.includes(tagName)) {
    removePendingTag(tagName);
  }
  
  setInputValue('');
  setIsOpen(false);
};

const removeTagFromSelection = (
  tagToRemove: string,
  selectedTags: string[],
  pendingTags: string[],
  onChange: (tags: string[]) => void,
  removePendingTag: (tagName: string) => void
) => {
  onChange(selectedTags.filter(tag => tag !== tagToRemove));
  
  if (pendingTags.includes(tagToRemove)) {
    removePendingTag(tagToRemove);
  }
};

const processTagAddition = (
  normalizedTagName: string,
  allTags: Tag[],
  addPendingTag: (tagName: string) => void,
  setInputValue: (value: string) => void,
  setIsOpen: (open: boolean) => void,
  handleTagSelect: (tagName: string) => void
) => {
  const existingTag = findExistingTag(allTags, normalizedTagName);
  
  if (existingTag) {
    handleTagSelect(existingTag.tagName);
  } else {
    addPendingTag(normalizedTagName);
    setInputValue('');
    setIsOpen(false);
  }
};

// Custom hook for event handlers to reduce complexity
const useTagSelectorHandlers = (
  readOnly: boolean,
  selectedTags: string[],
  inputValue: string,
  pendingTags: string[],
  allTags: Tag[],
  onChange: (tags: string[]) => void,
  addPendingTag: (tagName: string) => void,
  removePendingTag: (tagName: string) => void,
  setInputValue: (value: string) => void,
  setIsOpen: (open: boolean) => void,
  isOpen: boolean
) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s+/g, '');
    setInputValue(value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (!readOnly) setIsOpen(true);
  };

  const handleTagSelect = (tagName: string) => {
    if (!canSelectTag(readOnly, selectedTags, tagName)) return;
    
    addTagToSelection(
      tagName,
      selectedTags,
      pendingTags,
      onChange,
      removePendingTag,
      setInputValue,
      setIsOpen
    );
  };

  const handleAddPendingTag = () => {
    if (!canAddPendingTag(inputValue, readOnly)) return;
    
    const normalizedTagName = normalizeTagName(inputValue);
    if (!normalizedTagName) return;
    
    processTagAddition(
      normalizedTagName,
      allTags,
      addPendingTag,
      setInputValue,
      setIsOpen,
      handleTagSelect
    );
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (!canRemoveTag(readOnly)) return;
    
    removeTagFromSelection(
      tagToRemove,
      selectedTags,
      pendingTags,
      onChange,
      removePendingTag
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!canHandleKeyDown(readOnly)) return;

    if (shouldHandleEnterKey(e.key, inputValue)) {
      e.preventDefault();
      handleAddPendingTag();
    } else if (shouldHandleEscapeKey(e.key)) {
      setIsOpen(false);
    }
  };

  return {
    handleInputChange,
    handleInputFocus,
    handleTagSelect,
    handleAddPendingTag,
    handleRemoveTag,
    handleKeyDown
  };
};

// Custom hook for click outside handling
const useClickOutside = (containerRef: React.RefObject<HTMLDivElement | null>, setIsOpen: (open: boolean) => void) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef, setIsOpen]);
};

// Custom hook for imperative handle
const useImperativeHandleLogic = (
  ref: React.ForwardedRef<TagSelectorRef>,
  pendingTags: string[],
  processPendingTags: () => Promise<void>,
  tagsApi: any,
  setAllTags: (tags: Tag[]) => void
) => {
  useImperativeHandle(ref, () => ({
    getPendingTags: () => pendingTags,
    processPendingTags: async () => {
      await processPendingTags();
      const tags = await tagsApi.getTags();
      setAllTags(tags);
    }
  }));
};

export const TagSelector = React.forwardRef<TagSelectorRef, TagSelectorProps>(({
  selectedTags = [],
  onChange,
  readOnly = false,
  className = '',
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tagsApi = useTagsApi();

  // Use extracted hooks
  const { allTags, setAllTags, isLoading } = useTagData(tagsApi);
  const filteredTags = useTagFiltering(allTags, inputValue);
  const { pendingTags, addPendingTag, removePendingTag, processPendingTags } = usePendingTags(selectedTags, onChange, tagsApi);

  // Use custom hooks for handlers
  const handlers = useTagSelectorHandlers(
    readOnly,
    selectedTags,
    inputValue,
    pendingTags,
    allTags,
    onChange,
    addPendingTag,
    removePendingTag,
    setInputValue,
    setIsOpen,
    isOpen
  );

  // Use custom hooks for side effects
  useClickOutside(containerRef, setIsOpen);
  useImperativeHandleLogic(ref, pendingTags, processPendingTags, tagsApi, setAllTags);

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className={`flex flex-wrap gap-2 p-2 border rounded-md ${readOnly ? 'bg-gray-50' : 'bg-white'}`}>
        <SelectedTagsDisplay
          selectedTags={selectedTags}
          pendingTags={pendingTags}
          readOnly={readOnly}
          onRemoveTag={handlers.handleRemoveTag}
        />
        
        {!readOnly && (
          <div className="flex-1 min-w-[120px]">
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handlers.handleInputChange}
              onFocus={handlers.handleInputFocus}
              onKeyDown={handlers.handleKeyDown}
              placeholder="Type to search or add tags..."
              className="border-0 shadow-none focus-visible:ring-0 p-0 text-sm w-full"
            />
          </div>
        )}
      </div>

      <TagDropdown
        isOpen={isOpen}
        readOnly={readOnly}
        isLoading={isLoading}
        filteredTags={filteredTags}
        selectedTags={selectedTags}
        inputValue={inputValue}
        onTagSelect={handlers.handleTagSelect}
        onAddPendingTag={handlers.handleAddPendingTag}
      />
    </div>
  );
});

export default TagSelector;